<template>
  <div>
    <v-dialog
      v-model="dialog"
      max-width="650px"
      :fullscreen="$vuetify.display.smAndDown"
      scrollable
      :retain-focus="false"
    >
      <v-card v-if="vehicle">
        <!-- TITLE BAR -->
        <v-card-title
          class="redColor generate-qr-title d-flex align-center ga-2"
        >
          <span
            class="text-subtitle-1 font-weight-medium text-white flex-grow-1 generate-qr-title-text"
          >
            {{ $t("vehicle_entry.shipments.label") }}
          </span>
          <v-btn
            icon
            variant="outlined"
            color="white"
            class="generate-qr-close-btn flex-shrink-0"
            size="small"
            @click="close"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <!-- ALERT -->
          <v-alert
            v-if="alert.value"
            :type="alert.type"
            dense
            variant="tonal"
            class="mb-4"
          >
            {{ alert.message }}
          </v-alert>

          <!-- CAR DETAILS -->
          <v-row class="justify-center flex-wrap">
            <v-col cols="12" sm="3" class="d-flex flex-column min-width-0">
              <span class="caption">{{
                $t("parts.advanced_search.brand")
              }}</span>
              <span class="body-1 font-weight-medium">{{
                vehicle.brand.title
              }}</span>
            </v-col>

            <v-col cols="12" sm="4" class="d-flex flex-column min-width-0">
              <span class="caption">{{
                $t("parts.advanced_search.model")
              }}</span>
              <span class="body-1 font-weight-medium">{{
                vehicle.model.description
              }}</span>
            </v-col>

            <v-col cols="12" sm="5" class="d-flex flex-column min-width-0">
              <span class="caption">{{
                $t("parts.advanced_search.version")
              }}</span>
              <span class="body-1 font-weight-medium">{{
                vehicle.version.description
              }}</span>
            </v-col>
          </v-row>

          <!-- MAIN QR CODE -->
          <v-row justify="center" class="flex-wrap">
            <v-col cols="12" sm="6" md="6" class="justify-center text-center">
              <span>{{ $t("vehicle_entry.shipments.main_qr_code") }}</span>
              <qr-code
                class="d-flex justify-center text-center"
                :size="60"
                :text="vehicle._id"
              />
              <h4>{{ vehicle._id.slice(vehicle._id.length - 6) }}</h4>
            </v-col>

            <v-col cols="12" sm="6" class="d-flex flex-column align-center">
              <div
                v-if="selected"
                class="red--text body-1 font-weight-medium mb-2 text-center"
              >
                {{ $t("vehicle_entry.shipments.selectedAlert") }}
              </div>

              <v-row
                align="center"
                justify="center"
                class="generate-qr-add-row flex-wrap w-100 ma-0"
              >
                <v-col cols="12" sm="6" class="py-0">
                  <v-text-field
                    v-model="quantity"
                    :label="$t('parts.insert_parts.empty_parts.quantity')"
                    type="number"
                    min="1"
                    max="100"
                    variant="solo"
                    hide-details
                  />
                </v-col>
                <v-col cols="12" sm="6" class="py-0 d-flex align-center">
                  <v-btn
                    :loading="loadingAdd"
                    color="primary"
                    class="generate-qr-add-btn"
                    @click="addShipmentEntry"
                  >
                    {{ $t("vehicle_entry.shipments.add") }}
                    <v-icon end> mdi-plus </v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-col>
          </v-row>

          <!-- SHIPMENT QR CODE -->
          <v-row
            v-if="
              vehicle.shipment.id &&
              ($store.getters.role === 'LekoAdmin' ||
                $store.getters.role === 'SuperAdmin')
            "
          >
            <v-col cols="12" class="text-center">
              <span>{{ $t("vehicle_entry.shipments.shipment") }}</span>
              <qr-code :size="60" :text="vehicle.shipment.id._id" />
              <h4>
                {{
                  vehicle.shipment.id._id
                    ? vehicle.shipment.id._id.slice(
                        vehicle.shipment.id._id.length - 6,
                      )
                    : ""
                }}
              </h4>
            </v-col>
          </v-row>

          <!-- SHIPMENT ENTRIES TABLE -->
          <div class="generate-qr-table-wrap">
            <v-data-table
              :headers="headers"
              :items="vehicle.shipment.entries"
              :loading="loadingGet"
              item-key="_id"
              :items-per-page="-1"
              hide-default-footer
            >
              <template #loading>
                <v-progress-linear color="red-darken-2" indeterminate />
              </template>

              <template #item="{ item }">
                <tr>
                  <td class="generate-qr-date-cell">
                    <span class="generate-qr-date-desktop d-none d-md-inline">{{
                      formatDate(item.date)
                    }}</span>
                    <span class="generate-qr-date-mobile d-md-none">
                      <span class="d-block">{{
                        formatDatePart(item.date, "date")
                      }}</span>
                      <span class="d-block generate-qr-date-time">{{
                        formatDatePart(item.date, "time")
                      }}</span>
                    </span>
                  </td>
                  <td class="generate-qr-count-cell text-center">
                    {{ item.parts.length }}
                  </td>
                  <td class="generate-qr-actions-cell text-end">
                    <v-btn
                      icon
                      variant="text"
                      @click="viewQrCodes(vehicle, item.parts)"
                    >
                      <v-icon color="grey-darken-2">
                        mdi-format-list-bulleted
                      </v-icon>
                    </v-btn>

                    <v-btn
                      icon
                      variant="text"
                      :loading="loadingRemove"
                      @click="deleteShipmentEntry(item)"
                    >
                      <v-icon color="grey-darken-2"> mdi-delete </v-icon>
                    </v-btn>
                  </td>
                </tr>
              </template>
            </v-data-table>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="dialogDelete"
      max-width="420"
      :retain-focus="false"
    >
      <v-card>
        <v-card-title class="text-h6">
          <v-alert
            class="mb-0"
            :model-value="true"
            variant="tonal"
            type="error"
            border="bottom"
          >
            {{ $t("delete_alert") }}
          </v-alert>
        </v-card-title>

        <v-card-actions class="generate-qr-delete-actions pa-3">
          <v-spacer class="d-none d-sm-flex" />
          <v-btn variant="text" @click="dialogDelete = false">
            {{ $t("close") }}
          </v-btn>
          <v-btn color="red" variant="outlined" @click="confirmDelete()">
            {{ $t("delete") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from "moment";
import VueQrCode from "vue-qr-generator";

import getRequest from "../../api/getRequest";
import putRequest from "../../api/putRequest";
import deleteRequest from "../../api/deleteRequest";

export default {
  components: {
    "qr-code": VueQrCode,
  },
  data: () => ({
    dialog: false,
    loadingAdd: false,
    loadingGet: false,
    loadingRemove: false,
    vehicle: null,
    selected: null,
    quantity: 1,
    headers: [
      { title: "Date", sortable: false, value: "date" },
      { title: "Number of QR", sortable: false, value: "" },
      { title: "Actions", sortable: false, value: "" },
    ],
    alert: {
      value: false,
      type: "warning",
      message: "",
    },
    dialogDelete: false,
    entryToDelete: null,
  }),
  watch: {},
  created() {
    this.setHeaders();
  },
  methods: {
    setHeaders() {
      this.headers[0].text = this.$t("vehicle_entry.shipments.date");
      this.headers[1].text = this.$t("vehicle_entry.shipments.number_of_qr");
      this.headers[2].text = this.$t("vehicle_entry.shipments.actions");
    },
    formatDate(date) {
      if (!date) return "";
      moment.locale(localStorage.getItem("language") || "en");
      return moment(date).format("LLL");
    },
    formatDatePart(date, part) {
      if (!date) return "";
      moment.locale(localStorage.getItem("language") || "en");
      const formatted = moment(date);
      if (part === "date") return formatted.format("LL");
      if (part === "time") return formatted.format("LT");
      return formatted.format("LLL");
    },
    open(data) {
      this.getCar(data._id);
      this.dialog = true;
    },
    close() {
      this.dialog = false;
      this.vehicle = null;
      this.quantity = 1;
    },
    hide() {
      this.dialog = false;
    },
    show() {
      this.dialog = true;
    },
    getCar(id) {
      this.loadingGet = true;
      getRequest("/car", `id=${id}`)
        .then((response) => {
          this.vehicle = response.car;
        })
        .catch(() => {})
        .finally(() => {
          this.loadingGet = false;
        });
    },
    addShipmentEntry() {
      this.loadingAdd = true;
      putRequest("/car/entry", `id=${this.vehicle._id}`, {
        institutionId: this.$store.getters.institutionId,
        userId: this.$store.getters.userId,
        quantity: this.quantity,
        name: `${this.vehicle.brand.title} ${this.vehicle.model.description} ${this.vehicle.version.description}`,
        description: "",
        pra: this.vehicle.pra,
        withdrawnPIVA: this.vehicle.withdrawnPIVA,
      })
        .then((response) => {
          this.getCar(this.vehicle._id);
        })
        .catch(() => {})
        .finally(() => {
          this.loadingAdd = false;
        });
    },
    viewQrCodes(vehicle, parts) {
      this.$emit("view-qr-codes", { vehicle, parts });
    },
    deleteShipmentEntry(entry) {
      this.entryToDelete = entry;
      this.dialogDelete = true;
    },
    confirmDelete() {
      this.loadingRemove = true;
      deleteRequest(
        "/car/entry",
        `id=${this.vehicle._id}&entry_id=${this.entryToDelete._id}&user_id=${this.$store.getters.userId}`,
      )
        .then((response) => {
          if (response) {
            this.entryToDelete = null;
            this.dialogDelete = false;
            this.getCar(this.vehicle._id);
          }
        })
        .catch(() => {
          this.alert = {
            value: true,
            type: "warning",
            message: this.$t("vehicle_entry.shipments.deleteAlert"),
          };
          setTimeout(() => {
            this.alert = { value: false, type: "warning", message: "" };
          }, 2000);
        })
        .finally(() => {
          this.loadingRemove = false;
        });
    },
  },
};
</script>

<style scoped>
.generate-qr-title {
  padding: 12px 16px;
}

.generate-qr-title-text {
  min-width: 0;
  line-height: 1.35;
  word-break: break-word;
}

.min-width-0 {
  min-width: 0;
  word-break: break-word;
}

.generate-qr-actions-cell {
  white-space: nowrap;
}

@media (max-width: 959.98px) {
  .generate-qr-title {
    position: relative;
    padding: 12px 52px 12px 16px;
  }

  .generate-qr-close-btn {
    position: absolute;
    top: 50%;
    right: 12px;
    transform: translateY(-50%);
  }

  .generate-qr-add-btn {
    width: 100%;
  }

  .generate-qr-table-wrap {
    max-width: 100%;
    overflow-x: visible;
  }

  .generate-qr-table-wrap :deep(.v-table) {
    min-width: 0;
    width: 100%;
    table-layout: fixed;
  }

  .generate-qr-table-wrap :deep(th),
  .generate-qr-table-wrap :deep(td) {
    padding-left: 8px !important;
    padding-right: 8px !important;
  }

  .generate-qr-date-cell {
    width: 42%;
    white-space: normal;
    word-break: break-word;
    vertical-align: middle;
    line-height: 1.3;
  }

  .generate-qr-count-cell {
    width: 13%;
    vertical-align: middle;
  }

  .generate-qr-actions-cell {
    width: 45%;
    white-space: nowrap;
    vertical-align: middle;
  }

  .generate-qr-date-time {
    font-size: 0.75rem;
    opacity: 0.85;
  }

  .generate-qr-delete-actions {
    flex-wrap: wrap;
    justify-content: center;
    gap: 8px;
  }

  .generate-qr-delete-actions .v-btn {
    flex: 1 1 calc(50% - 8px);
    min-width: 0;
  }
}
</style>
